import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Cpu,
  Bot,
  Zap,
  Network,
  ShieldCheck,
  CheckCircle2,
  Workflow,
  Sparkles,
  Database,
  BarChart3,
  Layers,
  Search,
  Link2,
  Calendar,
  User,
  Lightbulb,
  Cog,
  TrendingUp,
  Box,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const APPROACH_STEPS = [
  {
    step: "01",
    icon: () => <Search className="w-5 h-5 text-[#0284C7]" strokeWidth={2.2} />,
    iconBg: "rgba(224, 242, 254, 1)",
    title: "Discover the Opportunity",
    desc: "Assess your business needs, identify use cases, and define goals.",
  },
  {
    step: "02",
    icon: () => <Box className="w-5 h-5 text-[#9333EA]" strokeWidth={2.2} />,
    iconBg: "rgba(243, 232, 255, 1)",
    title: "Build and Validate",
    desc: "Design, develop, and test AI agents with real data and workflows.",
  },
  {
    step: "03",
    icon: () => (
      <svg
        className="w-5 h-5 text-[#16A34A]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 20V10" />
        <path d="M18 20V4" />
        <path d="M6 20v-4" />
      </svg>
    ),
    iconBg: "rgba(220, 252, 231, 1)",
    title: "Deploy and Improve",
    desc: "Launch, monitor performance, and continuously optimize for greater impact.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Deploying domain-specific AI agents directly integrated with our core data sources gave us 3x faster response times and seamless automation across our enterprise.",
    name: "Sarah Jenkins",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Head of Operations • Apex Dynamics",
  },
  {
    quote:
      "Simpel's Agentic AI solution has helped us streamline our operations and improve overall efficiency. The team's expertise and support have been invaluable.",
    name: "Tejas Karwa",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Project Manager • Aptia UK",
  },
  {
    quote:
      "Automating complex multi-step workflows without human bottlenecks transformed our day-to-day execution and gave us complete auditable transparency.",
    name: "David Chen",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Head of Digital Transformation • Enterprise Systems",
  },
];

const WORK_PILLARS = [
  {
    icon: Search,
    iconColor: "#2563EB",
    iconBg: "#E0F2FE",
    titleLine1: "Understand the",
    titleLine2: "Work",
    desc: "We analyze your processes, data, and goals to identify high-value opportunities.",
  },
  {
    icon: Link2,
    iconColor: "#C026D3",
    iconBg: "#FAE8FF",
    titleLine1: "Connect Your",
    titleLine2: "Systems",
    desc: "Integrate with your existing tools, data sources, and enterprise platforms.",
  },
  {
    icon: Zap,
    iconColor: "#10B981",
    iconBg: "#DCFCE7",
    titleLine1: "Enable Intelligent",
    titleLine2: "Action",
    desc: "Deploy AI agents to perform tasks, make decisions, and orchestrate workflows.",
  },
];

const SERVICE_CAPABILITY_CARDS = [
  {
    icon: Calendar,
    iconColor: "#2563EB",
    iconBg: "rgba(224, 242, 254, 0.8)",
    title: "AI Agent Design",
    desc: "Create domain-specific AI agents tailored to your business needs.",
  },
  {
    icon: () => (
      <svg
        className="w-5 h-5 text-[#F97316]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="9" y="3" width="6" height="5" rx="1" />
        <rect x="3" y="16" width="6" height="5" rx="1" />
        <rect x="15" y="16" width="6" height="5" rx="1" />
        <path d="M12 8v4M6 16v-2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
      </svg>
    ),
    iconColor: "#F97316",
    iconBg: "rgba(255, 237, 213, 0.8)",
    title: "Workflow Orchestration",
    desc: "Coordinate multi-step processes across people, systems, and data.",
  },
  {
    icon: Database,
    iconColor: "#16A34A",
    iconBg: "rgba(220, 252, 231, 0.8)",
    title: "Knowledge Integration",
    desc: "Connect internal and external knowledge sources for accurate, context-aware responses.",
  },
  {
    icon: User,
    iconColor: "#9333EA",
    iconBg: "rgba(243, 232, 255, 0.8)",
    title: "Human-in-the-loop Controls",
    desc: "Keep people in control with approval workflows and governance.",
  },
  {
    icon: User,
    iconColor: "#D946EF",
    iconBg: "rgba(253, 232, 255, 0.8)",
    title: "Enterprise System Connectivity",
    desc: "Integrate with ERP, CRM, databases, APIs and more.",
  },
  {
    icon: BarChart3,
    iconColor: "#CA8A04",
    iconBg: "rgba(254, 249, 195, 0.8)",
    title: "Monitoring & Optimization",
    desc: "Track performance, gain insights, and continuously improve your agents.",
  },
];

const INTENT_TO_ACTION_STEPS = [
  {
    title: "Understand",
    desc: "Analyze context data, and goals",
    icon: Search,
    iconColor: "#2563EB",
    bgColor: "#BAE6FD",
    glowColor: "rgba(0, 210, 238, 0.5)",
  },
  {
    title: "Reason",
    desc: "Plan and make intelligent decisions",
    icon: Lightbulb,
    iconColor: "#C026D3",
    bgColor: "#F5D0FE",
    glowColor: "rgba(192, 38, 211, 0.5)",
  },
  {
    title: "Act",
    desc: "Execute tasks and orchestrate workflows",
    icon: Cog,
    iconColor: "#CA8A04",
    bgColor: "#FEF9C3",
    glowColor: "rgba(202, 138, 4, 0.5)",
  },
  {
    title: "Learn",
    desc: "Continuously improve with feedback",
    icon: TrendingUp,
    iconColor: "#16A34A",
    bgColor: "#BBF7D0",
    glowColor: "rgba(22, 163, 74, 0.5)",
  },
];

const AgenticAiPage = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePrevTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? TESTIMONIALS.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === TESTIMONIALS.length - 1 ? 0 : prev + 1
    );
  };

  const handleTalkToTeam = () => {
    openAgent("Agentic AI");
  };

  return (
    <div className="min-h-screen w-full bg-[#020B16] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black overflow-x-hidden">
      {/* Stack Navbar matching screenshot */}
      <StackNavbar />

      <main className="flex-1">
        {/* Hero Section matching User's Mockup */}
        <section className="relative w-full overflow-hidden bg-[#020B16] pt-[76px] sm:pt-[80px] min-h-[540px] sm:min-h-[580px] lg:min-h-[620px] xl:min-h-[660px] flex items-center">
          {/* Background Illustration spanning the full section, pinned to the right matching screenshot */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none z-0">
            <img
              src="/images/services/agentic-ai-hero.png"
              alt="Agentic AI for Smarter Business Outcomes"
              className="w-full h-full object-cover object-right pointer-events-none select-none block"
            />
          </div>

          {/* Subtle fade only on mobile/tablet screens for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#020B16] via-[#020B16]/80 to-transparent lg:hidden pointer-events-none z-[1]" />

          {/* Section Container with text content on the left */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-12 lg:py-16 flex items-center">
            <div className="max-w-[480px] xl:max-w-[520px] flex flex-col items-start">
              {/* Breadcrumb matching mockup: WHAT WE DO > SERVICES > AGENTIC AI */}
              <div className="flex items-center gap-2 text-[10.5px] sm:text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#7E93AC] select-none">
                <Link
                  to="/what-we-do"
                  className="hover:text-white transition-colors"
                >
                  WHAT WE DO
                </Link>
                <span>&gt;</span>
                <Link
                  to="/what-we-do#services"
                  className="hover:text-white transition-colors"
                >
                  SERVICES
                </Link>
                <span>&gt;</span>
                <span className="text-[#A2B6CE]">AGENTIC AI</span>
              </div>

              {/* Main Heading matching mockup */}
              <h1 className="mt-5 text-[32px] sm:text-[40px] lg:text-[46px] xl:text-[50px] font-extrabold leading-[1.14] tracking-tight text-white">
                <span className="block sm:whitespace-nowrap">Agentic AI for Smarter</span>
                <span className="block mt-1 sm:whitespace-nowrap">
                  <span className="text-[#00D2EE]">Business </span>
                  <span className="text-[#3B82F6]">Outcomes</span>
                </span>
              </h1>

              {/* Subtitle / Description */}
              <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[26px] text-[#9FB3C8] max-w-[490px] xl:max-w-[510px]">
                <span className="sm:block">Design and deploy intelligent AI agents that help your business make</span>
                <span className="sm:block">decisions, coordinate tasks, and streamline complex workflows.</span>
              </p>

              {/* CTA Button matching mockup */}
              <div className="mt-7 sm:mt-8">
                <button
                  onClick={handleTalkToTeam}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-[14px] sm:text-[14.5px] font-bold text-[#0B132B] shadow-[0_0_28px_rgba(0,180,255,0.45)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(0,210,238,0.65)] active:scale-95 cursor-pointer"
                >
                  <span>Talk to our team</span>
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                    strokeWidth={2.4}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Intelligent by Design Section matching user screenshot */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[84px] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-12 lg:gap-10">
            {/* Left Content Column */}
            <div className="w-full lg:w-[480px] xl:w-[510px] shrink-0 pt-1">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                INTELLIGENT BY DESIGN
              </p>
              <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[38px] xl:text-[42px] font-extrabold leading-[1.15] tracking-tight text-[#0F172A]">
                <span className="block text-[#0F172A] sm:whitespace-nowrap">AI agents built around</span>
                <span className="block bg-gradient-to-r from-[#00D2EE] to-[#2563EB] bg-clip-text text-transparent sm:whitespace-nowrap">
                  how your business works.
                </span>
              </h2>
              <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#556987] max-w-[440px]">
                Simpel's Agentic AI solutions are designed to understand your business context, connect across your systems, and take intelligent action — helping you move faster, operate smarter, and create lasting value.
              </p>
            </div>

            {/* Right Cards Column */}
            <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-3 gap-5 xl:gap-6">
              {WORK_PILLARS.map((pillar) => {
                const IconComp = pillar.icon;
                return (
                  <div
                    key={pillar.titleLine1}
                    className="flex flex-col justify-between p-7 sm:p-7 xl:p-8 rounded-[22px] bg-white border border-[#E2E8F0]/90 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_36px_rgba(37,99,235,0.08)] hover:-translate-y-1 transition-all duration-300 select-none min-h-[250px] sm:min-h-[270px]"
                  >
                    <div>
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: pillar.iconBg }}
                      >
                        <IconComp
                          className="w-5 h-5"
                          style={{ color: pillar.iconColor }}
                          strokeWidth={2.4}
                        />
                      </div>

                      <h3 className="mt-6 text-[18px] sm:text-[19px] font-bold text-[#0F172A] leading-snug tracking-tight">
                        <span className="block">{pillar.titleLine1}</span>
                        <span className="block">{pillar.titleLine2}</span>
                      </h3>

                      <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                        {pillar.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Service Capabilities Section matching user and Figma screenshots */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(236, 245, 253, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px]">
            {/* Header Block */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 lg:gap-12 pb-12 sm:pb-14">
              <div className="max-w-[620px]">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                  SERVICE CAPABILITIES
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[40px] xl:text-[44px] font-extrabold leading-[1.15] tracking-tight text-[#0F172A]">
                  <span className="block sm:whitespace-nowrap">From repetitive tasks to intelligent</span>
                  <span className="block">workflows.</span>
                </h2>
              </div>

              <div className="lg:border-l lg:border-[#CBD5E1] lg:pl-8 max-w-[440px]">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#556987]">
                  Build, deploy, and scale AI agents that understand your business, connect with existing systems, and automate complex workflows securely and efficiently.
                </p>
              </div>
            </div>

            {/* 6 Cards Grid matching exact Figma specs: Width 411px, Height 235px, Radius 16px, Border 1px, Padding 20px, Gap 10px */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 xl:gap-[20px] w-full">
              {SERVICE_CAPABILITY_CARDS.map((card) => {
                const IconComponent = card.icon;
                return (
                  <div
                    key={card.title}
                    className="flex flex-col justify-between select-none transition-all duration-300 hover:shadow-[0_12px_36px_rgba(37,99,235,0.08)] hover:-translate-y-1"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 1)",
                      borderRadius: "16px",
                      border: "1px solid rgba(226, 232, 240, 1)",
                      minHeight: "235px",
                      padding: "20px",
                      gap: "10px",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
                    }}
                  >
                    {/* Top Row: Icon badge */}
                    <div className="flex items-center w-full">
                      <div
                        className="w-[42px] h-[42px] rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: card.iconBg }}
                      >
                        {typeof card.icon === "function" && !card.icon.name ? (
                          <card.icon />
                        ) : (
                          <IconComponent
                            className="w-5 h-5"
                            style={{ color: card.iconColor }}
                            strokeWidth={2.2}
                          />
                        )}
                      </div>
                    </div>

                    {/* Bottom Content: Title and Description */}
                    <div>
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug tracking-tight">
                        {card.title}
                      </h3>
                      <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[20px] text-[#64748B]">
                        {card.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* From Intent to Action Section matching user screenshots */}
        <section className="relative w-full overflow-hidden bg-[#020B16] py-16 sm:py-20 lg:py-[88px]">
          {/* Background Image from user */}
          <div className="absolute inset-0 z-0 pointer-events-none select-none overflow-hidden">
            <img
              src="/images/services/intent-to-action-bg.png"
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header Block */}
            <div className="max-w-[680px]">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#A0AEC0]">
                FROM INTENT TO IMPACT
              </p>
              <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[44px] font-extrabold tracking-tight text-white leading-[1.15]">
                <span>From Intent to </span>
                <span className="text-[#00D2EE]">Action.</span>
              </h2>
              <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#94A3B8] max-w-[500px]">
                Our Agentic AI solutions follow a proven approach to understand, reason, act, and learn — delivering measurable business impact.
              </p>
            </div>

            {/* 4 Steps Timeline matching Screenshot 1 */}
            <div className="mt-14 sm:mt-16 w-full flex flex-col md:flex-row items-start justify-between gap-8 md:gap-3 lg:gap-4">
              {INTENT_TO_ACTION_STEPS.map((item, idx) => {
                const IconComp = item.icon;
                const isLast = idx === INTENT_TO_ACTION_STEPS.length - 1;
                return (
                  <div key={item.title} className="flex-1 flex items-start w-full">
                    <div className="flex-1 flex flex-col items-center text-center select-none group">
                      {/* Circular Glowing Icon Node */}
                      <div
                        className="w-[48px] h-[48px] rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110"
                        style={{
                          backgroundColor: item.bgColor,
                          boxShadow: `0 0 24px ${item.glowColor}`,
                        }}
                      >
                        <IconComp
                          className="w-5 h-5"
                          style={{ color: item.iconColor }}
                          strokeWidth={2.4}
                        />
                      </div>

                      {/* Title */}
                      <h3 className="mt-5 text-[17px] sm:text-[18px] font-bold text-white tracking-tight leading-snug">
                        {item.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-[12px] sm:text-[13px] leading-[19px] text-[#9FB3C8] max-w-[200px]">
                        {item.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps */}
                    {!isLast && (
                      <div className="hidden md:flex items-center justify-center pt-3.5 shrink-0 px-2 lg:px-4">
                        <ArrowRight
                          className="w-5 h-5 text-[#00D2EE]"
                          strokeWidth={2.2}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Built for Business Section matching user screenshot */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-14">
            {/* Left Column: Laptop / Dashboard Graphic */}
            <div className="w-full lg:w-[500px] xl:w-[560px] shrink-0">
              <div className="relative rounded-[20px] overflow-hidden shadow-[0_16px_40px_rgba(0,0,0,0.12)] border border-slate-200/90 bg-[#020B16]">
                <img
                  src="/images/services/business-operations.png"
                  alt="Built for real business operations - Simpel Agent Workflow"
                  className="w-full h-auto object-cover select-none block"
                />
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="flex-1 w-full max-w-[580px] flex flex-col items-start">
              {/* Eyebrow */}
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                BUILT FOR BUSINESS
              </p>

              {/* Main Heading */}
              <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] xl:text-[44px] font-extrabold leading-[1.14] tracking-tight text-[#0F172A]">
                <span>Built for real </span>
                <span className="bg-gradient-to-r from-[#00D2EE] to-[#2563EB] bg-clip-text text-transparent">
                  business
                </span>
                <br />
                <span className="bg-gradient-to-r from-[#00D2EE] to-[#2563EB] bg-clip-text text-transparent">
                  operations.
                </span>
              </h2>

              {/* Description */}
              <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#556987] max-w-[480px]">
                Our Agentic AI solutions are designed to fit into your existing processes and systems, helping your teams work more efficiently and make better decisions.
              </p>

              {/* 3 Key Benefits */}
              <div className="mt-8 flex flex-col gap-4 w-full">
                {/* Item 1 */}
                <div className="flex items-center gap-3.5 sm:gap-4">
                  <div className="w-10 h-10 rounded-[12px] bg-[#EFF6FF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
                    <Zap className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                  </div>
                  <span className="text-[14.5px] sm:text-[15.5px] font-bold text-[#0F172A] tracking-tight">
                    Reduce manual handoffs
                  </span>
                </div>

                {/* Item 2 */}
                <div className="flex items-center gap-3.5 sm:gap-4">
                  <div className="w-10 h-10 rounded-[12px] bg-[#EFF6FF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
                    <svg
                      className="w-4 h-4 text-[#2563EB]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect width="20" height="16" x="2" y="4" rx="3" />
                      <path d="M2 12h5l2 2h6l2-2h5" />
                    </svg>
                  </div>
                  <span className="text-[14.5px] sm:text-[15.5px] font-bold text-[#0F172A] tracking-tight">
                    Improve access to business knowledge
                  </span>
                </div>

                {/* Item 3 */}
                <div className="flex items-center gap-3.5 sm:gap-4">
                  <div className="w-10 h-10 rounded-[12px] bg-[#EFF6FF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
                    <BarChart3 className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                  </div>
                  <span className="text-[14.5px] sm:text-[15.5px] font-bold text-[#0F172A] tracking-tight">
                    Support faster, more consistent decisions
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Designed for Your Ecosystem Section */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(248, 250, 252, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-14">
            {/* Left Column: Text */}
            <div className="w-full lg:w-[460px] xl:w-[490px] shrink-0 flex flex-col items-start">
              {/* Eyebrow */}
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                DESIGNED FOR YOUR ECOSYSTEM
              </p>

              {/* Main Heading */}
              <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] xl:text-[44px] font-extrabold leading-[1.14] tracking-tight text-[#0F172A]">
                <span className="block text-[#0F172A]">Connect. Extend.</span>
                <span className="block text-[#00D2EE]">Do More.</span>
              </h2>

              {/* Description */}
              <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[26px] text-[#556987] max-w-[440px]">
                Easily integrate with your existing systems and data sources. Our Agentic AI solutions work across your technology landscape to deliver end-to-end automation.
              </p>
            </div>

            {/* Right Column: Ecosystem Graphic */}
            <div className="flex-1 w-full flex justify-center lg:justify-end items-center">
              <div className="relative w-full max-w-[620px] xl:max-w-[680px]">
                <img
                  src="/images/services/agent-ecosystem-diagram.png"
                  alt="Designed for your ecosystem - Connect, Extend, Do More"
                  className="w-full h-auto object-contain select-none block"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach Section */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12">
            {/* Left Column: Heading & Text */}
            <div className="w-full lg:w-[320px] xl:w-[360px] shrink-0 flex flex-col items-start">
              {/* Eyebrow */}
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                OUR APPROACH
              </p>

              {/* Main Heading */}
              <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] font-extrabold leading-[1.14] tracking-tight text-[#0F172A]">
                A clear path from opportunity to impact.
              </h2>

              {/* Description */}
              <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#556987] max-w-[340px]">
                We follow a structured approach to design, deploy, and scale AI agents that deliver measurable value.
              </p>
            </div>

            {/* Right Column: 3 Step Cards */}
            <div className="flex-1 w-full flex flex-col sm:flex-row items-center lg:justify-end gap-5 lg:gap-5 xl:gap-6 justify-center">
              {APPROACH_STEPS.map((step) => {
                const IconComp = step.icon;
                return (
                  <div
                    key={step.step}
                    className="relative w-full sm:w-[271.56px] min-h-[236.5px] p-6 rounded-[16px] flex flex-col justify-between overflow-hidden"
                    style={{
                      backgroundColor: "rgba(248, 250, 252, 1)",
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                    }}
                  >
                    {/* Top-Right Number Badge */}
                    <div className="absolute top-0 right-0 w-[48px] h-[32px] bg-[#00D2EE] text-white font-extrabold text-[15px] tracking-tight flex items-center justify-center rounded-bl-[14px] select-none">
                      {step.step}
                    </div>

                    {/* Top Content: Icon & Text */}
                    <div>
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: step.iconBg }}
                      >
                        <IconComp />
                      </div>

                      {/* Title */}
                      <h3 className="mt-6 text-[17px] font-bold text-[#0F172A] tracking-tight leading-snug">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2.5 text-[13px] leading-[20px] text-[#556987]">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Real Solutions. Real Impact. Section matching Figma specs */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(248, 250, 252, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col items-center">
            {/* Eyebrow */}
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B] text-center">
              TRUSTED BY BUSINESSES THAT INNOVATE
            </p>

            {/* Main Heading */}
            <h2 className="mt-3.5 text-[32px] sm:text-[40px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-center">
              <span className="text-[#0F172A]">Real Solutions. </span>
              <span className="text-[#1D70F7]">Real Impact.</span>
            </h2>

            {/* Description */}
            <p className="mt-4 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#556987] text-center max-w-[660px]">
              We partner with organizations across industries to deploy AI agents that drive efficiency, improve decision-making, and create measurable business value.
            </p>

            {/* Testimonial Card with Prev/Next Controls */}
            <div className="mt-12 sm:mt-14 w-full flex items-center justify-center gap-3 sm:gap-6 lg:gap-8">
              {/* Prev Button */}
              <button
                onClick={handlePrevTestimonial}
                aria-label="Previous testimonial"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Center Card: Hug (672px max-w), Radius: 24px, Padding: 40px, Gap: 15.5px, Colors: rgba(255, 255, 255, 1), Border: 1px rgba(226, 232, 240, 1), Shadow: 0 1px 2px rgba(0,0,0,0.05) */}
              <div
                className="w-full max-w-[672px] p-7 sm:p-[40px] rounded-[24px] flex flex-col gap-[15.5px] border"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  borderColor: "rgba(226, 232, 240, 1)",
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Quote Icon */}
                <div className="text-[#1D70F7]">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>

                {/* Quote Text */}
                <p className="text-[15px] sm:text-[16.5px] leading-[26px] sm:leading-[28px] text-[#334155] italic">
                  “{TESTIMONIALS[activeTestimonial].quote}”
                </p>

                {/* Author Info matching Screenshot 1 */}
                <div className="mt-3 flex items-center gap-4">
                  {TESTIMONIALS[activeTestimonial].image ? (
                    <div className="w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] rounded-full border-[3px] border-[#00D2EE] overflow-hidden shrink-0 shadow-sm">
                      <img
                        src={TESTIMONIALS[activeTestimonial].image}
                        alt={TESTIMONIALS[activeTestimonial].name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] rounded-full bg-[#E2E8F0] flex items-center justify-center font-bold text-[15px] text-[#64748B] shrink-0 select-none">
                      {TESTIMONIALS[activeTestimonial].initials}
                    </div>
                  )}
                  <div className="flex flex-col">
                    <span className="font-bold text-[19px] sm:text-[22px] text-[#0F172A] leading-tight tracking-tight">
                      {TESTIMONIALS[activeTestimonial].name}
                    </span>
                    <span className="text-[14px] sm:text-[15.5px] text-[#5A6E85] mt-1 font-medium">
                      {TESTIMONIALS[activeTestimonial].role}
                    </span>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextTestimonial}
                aria-label="Next testimonial"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer shrink-0"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="mt-8 flex items-center justify-center gap-2">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    activeTestimonial === idx
                      ? "w-6 h-2 bg-[#1D70F7]"
                      : "w-2 h-2 bg-[#BFDBFE] hover:bg-[#93C5FD]"
                  }`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Ready to Unlock New Possibilities Globe CTA Section */}
        <section
          id="cta"
          className="relative w-full overflow-hidden flex items-center py-20 sm:py-24 lg:py-28"
          style={{ backgroundColor: "#000E1B", minHeight: 440 }}
        >
          {/* Background globe image provided by user with BOLDER BRIGHTER TOGETHER baked in */}
          <img
            src="/images/services/agentic-ai-globe-banner.png"
            alt="Let's put AI agents to work"
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-right lg:object-center pointer-events-none select-none"
          />

          {/* Subtle overlay on smaller screens for crisp text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#000E1B]/90 via-[#000E1B]/50 to-transparent lg:hidden pointer-events-none" />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
              {/* Left Content */}
              <div className="max-w-[780px]">
                {/* Eyebrow */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#8EA3BD]">
                  READY TO UNLOCK NEW POSSIBILITIES
                </p>

                {/* Heading */}
                <h2 className="mt-4 text-[28px] sm:text-[36px] lg:text-[42px] xl:text-[48px] font-extrabold text-white leading-[1.12] tracking-tight sm:whitespace-nowrap">
                  <span>Let’s put AI </span>
                  <span className="text-[#00D2EE]">agents to work?</span>
                </h2>

                {/* Paragraph */}
                <p className="mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#A2B6CE] max-w-[480px]">
                  Let’s explore where intelligent automations can create value for your business.
                </p>

                {/* CTA Button */}
                <div className="mt-8">
                  <button
                    onClick={handleTalkToTeam}
                    className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-bold text-[#04101D] transition-all duration-300 hover:scale-[1.03] active:scale-95 cursor-pointer"
                    style={{
                      boxShadow:
                        "0 0 24px 3px rgba(47, 115, 246, 0.75), 0 0 45px rgba(0, 210, 238, 0.35)",
                    }}
                  >
                    <span>Talk to Our Team</span>
                    <ArrowRight
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                      strokeWidth={2.2}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AgenticAiPage;
